import { Link, useNavigate } from "react-router-dom";
import { useAuth, useWS } from "../App";
import { Home, Search, Heart, User, MessageCircle, LogOut, Settings, ShoppingCart, BarChart3, Users, Wifi, WifiOff, Package, HelpCircle, Gift, Image, Download, Smartphone, Megaphone } from "lucide-react";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import TutorialModal, { useTutorial } from "./TutorialModal";
import InlineSearch from "./InlineSearch";
import { TopBar } from "./TopBar";
import { toast } from "sonner";

const useMobileLayoutToggle = (user, token) => {
  const [mobileLayout, setMobileLayout] = useState("classic");
  useEffect(() => {
    if (user?.is_admin) {
      axios.get(`${API}/settings/mobile-layout`).then((r) => setMobileLayout(r.data.layout)).catch(() => {});
    }
  }, [user]);
  const toggleMobileLayout = async () => {
    const next = mobileLayout === "banner" ? "classic" : "banner";
    try {
      await axios.put(`${API}/admin/settings/mobile-layout`, { layout: next }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setMobileLayout(next);
      toast.success(next === "banner" ? "Diseño móvil cambiado a Feed Banner" : "Diseño móvil cambiado a Clásico");
      setTimeout(() => window.location.reload(), 700);
    } catch {
      toast.error("Error al cambiar el diseño");
    }
  };
  return { mobileLayout, toggleMobileLayout };
};

const useTopBarToggle = (user, token) => {
  const { topbarEnabled, setTopbarEnabled } = useAuth();
  const toggleTopbar = async () => {
    const next = !topbarEnabled;
    try {
      await axios.put(`${API}/admin/settings/topbar`, { enabled: next }, {
        headers: { Authorization: `Bearer ${token}` }, withCredentials: true
      });
      setTopbarEnabled(next);
      toast.success(next ? "Encabezado con movimiento activado" : "Encabezado con movimiento desactivado");
    } catch {
      toast.error("Error al cambiar el encabezado");
    }
  };
  return { topbarEnabled, toggleTopbar };
};

export const Header = () => {
  const { user, logout, token, loading: authLoading } = useAuth();
  const { mobileLayout, toggleMobileLayout } = useMobileLayoutToggle(user, token);
  const { topbarEnabled, toggleTopbar } = useTopBarToggle(user, token);
  const ws = useWS();
  const navigate = useNavigate();
  const [showTutorial, setShowTutorial] = useState(false);
  const [apkConfig, setApkConfig] = useState(null);

  const LOGO_URL = "/logo-marketvalley.png"; // Previous IBiiD logo preserved at /logo-ibiid-backup.png

  // Fetch APK + branding config on mount
  useEffect(() => {
    fetchApkConfig();
  }, []);

  const fetchApkConfig = async () => {
    try {
      const response = await axios.get(`${API}/app-config`);
      setApkConfig(response.data);
      if (response.data.logo_url) {
        localStorage.setItem('brand_logo_url', response.data.logo_url);
      } else {
        localStorage.removeItem('brand_logo_url');
      }
    } catch (error) {
      console.error("Error fetching APK config:", error);
    }
  };

  // Auto-show tutorial for new users (skip for admins - the tour targets buyer flows)
  useEffect(() => {
    if (user && !user.is_admin && !localStorage.getItem('ibiid_tutorial_seen')) {
      // Show tutorial after a short delay for new users
      const timer = setTimeout(() => {
        setShowTutorial(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <>
      {topbarEnabled && <TopBar />}
      <header className="app-header" data-testid="app-header">
      <div className="max-w-5xl mx-auto px-4">
        {/* Row 1: Logo + Flag left, WebSocket indicator right */}
        <div className="flex items-center justify-between h-12 sm:h-[68px]">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={apkConfig?.logo_url || LOGO_URL} alt="Clueer" className="h-8 sm:h-[60px] w-auto ml-1" />
            <span className="text-[8px] ml-0.5 relative -top-2 sm:-top-5" title="Panamá">🇵🇦</span>
          </Link>
          
          <div className="flex items-center gap-2">
            {/* APK Download Button - visible to all users, positioned far left */}
            {apkConfig?.apk_url && (
              <a
                href={apkConfig.apk_url}
                download={apkConfig.apk_filename || "ibiid-app.apk"}
                className="flex items-center gap-1 px-2 py-1 text-[#0F172A] hover:text-[#F97316] transition-colors group mr-2"
                title="Descarga nuestra APP Android"
                data-testid="header-download-apk"
              >
                <Smartphone size={14} className="group-hover:animate-bounce flex-shrink-0" />
                <span className="text-[9px] font-bold whitespace-nowrap">
                  App Android
                </span>
              </a>
            )}
            
            {/* FAQ Button - always visible */}
            <button
              onClick={() => setShowTutorial(true)}
              className="p-1 text-[#64748B] hover:text-[#0F172A] transition-colors"
              title="Ayuda y Tutorial"
              data-testid="header-faq-btn"
            >
              <HelpCircle size={18} />
            </button>
            
            {/* WebSocket indicator */}
            {user && (
              <div 
                className="flex items-center gap-1"
                title={ws?.isConnected ? "Conectado en tiempo real" : "Modo offline"}
              >
                {ws?.isConnected ? (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                  </span>
                ) : (
                  <span className="relative flex h-2 w-2">
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Row 2: Search bar + icons */}
        <div className="flex items-center gap-3 pb-3">
          {/* Inline Search - shows results directly */}
          <InlineSearch />
          
          {user ? (
            <>
              {/* Admin icons - Ofertas and Productos */}
              {user.is_admin && (
                <>
                  <Link
                    to="/admin/active-offers"
                    className="relative p-2 text-[#374151] hover:text-[#0F172A] transition-colors"
                    data-testid="header-admin-offers"
                    title="Ofertas Activas"
                  >
                    <BarChart3 size={22} />
                  </Link>
                  <Link
                    to="/admin/products"
                    className="relative p-2 text-[#374151] hover:text-[#0F172A] transition-colors"
                    data-testid="header-admin-products"
                    title="Productos"
                  >
                    <Package size={22} />
                  </Link>
                </>
              )}
              
              {/* Profile avatar - Desktop only */}
              <div className="hidden md:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="focus:outline-none" data-testid="user-menu-trigger">
                      <Avatar className="h-9 w-9 cursor-pointer hover:ring-2 hover:ring-[#475569] transition-all">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-[#475569] text-white text-sm font-semibold">
                          {user.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to={`/profile/${user.user_id}`} className="flex items-center gap-2" data-testid="menu-profile">
                        <User size={16} />
                        Mi Perfil
                      </Link>
                    </DropdownMenuItem>
                    {user.is_admin ? (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/active-offers" className="flex items-center gap-2">
                            <BarChart3 size={16} />
                            Ofertas Activas
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/products" className="flex items-center gap-2">
                            <Package size={16} />
                            Administrar Productos
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/inventory" className="flex items-center gap-2">
                            <Package size={16} />
                            Inventario Privado
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/banners" className="flex items-center gap-2">
                            <Image size={16} />
                            Banners
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/messaging" className="flex items-center gap-2" data-testid="menu-admin-messaging">
                            <MessageCircle size={16} />
                            Mensajes y Promos
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={toggleMobileLayout} className="flex items-center gap-2 cursor-pointer" data-testid="menu-mobile-layout-toggle">
                          <Smartphone size={16} />
                          {mobileLayout === "banner" ? "Diseño móvil: Feed Banner ✓" : "Diseño móvil: Clásico ✓"}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={toggleTopbar} className="flex items-center gap-2 cursor-pointer" data-testid="menu-topbar-toggle">
                          <Megaphone size={16} />
                          {topbarEnabled ? "Encabezado con movimiento: Activado ✓" : "Encabezado con movimiento: Desactivado"}
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/app-config" className="flex items-center gap-2">
                            <Smartphone size={16} />
                            APP Android
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/clients" className="flex items-center gap-2">
                            <Users size={16} />
                            Panel de Clientes
                          </Link>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/my-purchases" className="flex items-center gap-2">
                            <ShoppingCart size={16} />
                            Mis Compras
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/favorites" className="flex items-center gap-2">
                            <Heart size={16} />
                            Favoritos
                          </Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowTutorial(true)} className="flex items-center gap-2">
                      <HelpCircle size={16} />
                      Tutorial / Ayuda
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                      <LogOut size={16} className="mr-2" />
                      Cerrar Sesión
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </>
          ) : (
            <Link to="/login?tab=whatsapp">
              <Button size="sm" className="rounded-full bg-[#34D399] hover:bg-[#10B981] text-xs px-4" data-testid="login-btn">
                Acceder
              </Button>
            </Link>
          )}
        </div>
      </div>
      
      {/* Tutorial Modal */}
      <TutorialModal isOpen={showTutorial} onClose={() => setShowTutorial(false)} />
    </header>
    </>
  );
};

export const BottomNav = () => {
  const { user, token, logout } = useAuth();
  const { mobileLayout, toggleMobileLayout } = useMobileLayoutToggle(user, token);
  const { topbarEnabled, toggleTopbar } = useTopBarToggle(user, token);
  const navigate = useNavigate();
  const [purchaseActivity, setPurchaseActivity] = useState(0);
  const location = window.location.pathname;

  useEffect(() => {
    if (user && token && !user.is_admin) {
      fetchPurchaseActivity();
    }
  }, [user, token]);

  const fetchPurchaseActivity = async () => {
    try {
      const response = await axios.get(`${API}/my/activity-count`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setPurchaseActivity(response.data.count);
    } catch (error) {
      console.error("Error fetching purchase activity:", error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (!user) return null;

  const isActive = (path) => location === path || location.startsWith(path + '/');

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-[#E2E8F0]/50 z-50 md:hidden" data-testid="bottom-nav">
      <div className="max-w-md mx-auto px-8 py-1.5 flex items-center justify-between">
        <Link to="/" className={`p-2 ${isActive('/') && location === '/' ? 'text-[#0F172A]' : 'text-[#94A3B8]'}`} data-testid="bnav-home">
          <Home size={20} strokeWidth={isActive('/') && location === '/' ? 2.5 : 1.5} />
        </Link>
        
        <Link to="/search" className={`p-2 ${isActive('/search') ? 'text-[#0F172A]' : 'text-[#94A3B8]'}`} data-testid="bnav-search">
          <Search size={20} strokeWidth={isActive('/search') ? 2.5 : 1.5} />
        </Link>
        
        <Link to={user.is_admin ? "/admin/reviews" : "/my-rewards"} className="p-2 text-[#F97316]" data-testid="bnav-rewards">
          <Gift size={20} strokeWidth={2} />
        </Link>
        
        <Link to="/favorites" className={`p-2 ${isActive('/favorites') ? 'text-[#0F172A]' : 'text-[#94A3B8]'}`} data-testid="bnav-favorites">
          <Heart size={20} strokeWidth={isActive('/favorites') ? 2.5 : 1.5} />
        </Link>
        
        {/* Profile with dropdown menu - opens upward */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1 focus:outline-none" data-testid="bnav-profile">
              <Avatar className="h-7 w-7 cursor-pointer ring-2 ring-transparent hover:ring-[#475569] transition-all">
                <AvatarImage src={user.picture} alt={user.name} />
                <AvatarFallback className="bg-[#475569] text-white text-xs font-semibold">
                  {user.name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" side="top" className="w-48 mb-2">
            <DropdownMenuItem asChild>
              <Link to={`/profile/${user.user_id}`} className="flex items-center gap-2">
                <User size={16} />
                Mi Perfil
              </Link>
            </DropdownMenuItem>
            {user.is_admin ? (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/admin/active-offers" className="flex items-center gap-2">
                    <BarChart3 size={16} />
                    Ofertas Activas
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/products" className="flex items-center gap-2">
                    <Package size={16} />
                    Administrar Productos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/inventory" className="flex items-center gap-2">
                    <Package size={16} />
                    Inventario Privado
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/banners" className="flex items-center gap-2">
                    <Image size={16} />
                    Banners
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/messaging" className="flex items-center gap-2" data-testid="bnav-admin-messaging">
                    <MessageCircle size={16} />
                    Mensajes y Promos
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleMobileLayout} className="flex items-center gap-2 cursor-pointer" data-testid="bnav-mobile-layout-toggle">
                  <Smartphone size={16} />
                  {mobileLayout === "banner" ? "Diseño móvil: Feed Banner ✓" : "Diseño móvil: Clásico ✓"}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleTopbar} className="flex items-center gap-2 cursor-pointer" data-testid="bnav-topbar-toggle">
                  <Megaphone size={16} />
                  {topbarEnabled ? "Encabezado con movimiento: Activado ✓" : "Encabezado con movimiento: Desactivado"}
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/app-config" className="flex items-center gap-2">
                    <Smartphone size={16} />
                    APP Android
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/clients" className="flex items-center gap-2">
                    <Users size={16} />
                    Panel de Clientes
                  </Link>
                </DropdownMenuItem>
              </>
            ) : (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/my-purchases" className="flex items-center gap-2">
                    <ShoppingCart size={16} />
                    Mis Compras
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/favorites" className="flex items-center gap-2">
                    <Heart size={16} />
                    Favoritos
                  </Link>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-red-600">
              <LogOut size={16} className="mr-2" />
              Cerrar Sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  );
};

export default Header;
